/**
 * Utility function for conditionally joining CSS class names
 * Similar to the popular clsx library but tailored for this project
 */

type ClassValue = string | number | boolean | undefined | null | ClassDict | ClassArray;
type ClassDict = Record<string, any>;
type ClassArray = ClassValue[];

/**
 * Conditionally joins CSS class names together
 * @param inputs - Class names, objects with boolean values, or arrays
 * @returns A string of space-separated class names
 *
 * @example
 * // Basic usage
 * classNames('foo', 'bar'); // => 'foo bar'
 *
 * // Conditional classes
 * classNames('foo', condition && 'bar'); // => 'foo bar' (if condition is true)
 * classNames('foo', condition && 'bar'); // => 'foo' (if condition is false)
 *
 * // Object syntax
 * classNames('foo', { bar: true, baz: false }); // => 'foo bar'
 *
 * // Mixed usage
 * classNames('btn', 'btn-primary', {
 *   'btn-large': isLarge,
 *   'btn-disabled': isDisabled
 * }); // => 'btn btn-primary btn-large' (if isLarge is true, isDisabled is false)
 *
 * // Array syntax
 * classNames(['foo', 'bar'], 'baz'); // => 'foo bar baz'
 *
 * // Real-world example from CmsLink
 * classNames('cart-button-wrap', { 'full-width': fullWidth });
 */
/**
 * Processes a single input value and returns class names
 */
function processInput(input: ClassValue): string[] {
  if (!input) {
    return [];
  }

  const inputType = typeof input;

  if (inputType === 'string' || inputType === 'number') {
    // Only allow string or number, avoid stringifying objects
    return [inputType === 'string' ? (input as string) : String(input)];
  }

  if (Array.isArray(input)) {
    const nestedResult = classNames(...input);
    return nestedResult ? [nestedResult] : [];
  }

  if (inputType === 'object') {
    const obj = input as ClassDict;
    const classes: string[] = [];
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key]) {
        classes.push(key);
      }
    }
    return classes;
  }

  return [];
}

export function classNames(...inputs: ClassValue[]): string {
  const allClasses: string[] = [];

  for (const input of inputs) {
    const classes = processInput(input);
    allClasses.push(...classes);
  }

  return allClasses.join(' ');
}

/**
 * A shorter alias for classNames
 */
export const cn = classNames;

/**
 * Utility for creating conditional class strings with base classes
 * @param baseClasses - Always included base classes
 * @param conditionalClasses - Conditionally applied classes
 * @returns A string of space-separated class names
 *
 * @example
 * conditionalClassNames('btn btn-primary', {
 *   'btn-large': isLarge,
 *   'btn-disabled': isDisabled
 * });
 */
export function conditionalClassNames(baseClasses: string, conditionalClasses: ClassDict): string {
  return classNames(baseClasses, conditionalClasses);
}
